/** AgentPromptEditor — edit, Save Draft (inactive), Publish (activate). */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from '@/shared/ui/sonner';
import { getUserErrorMessage } from '@/shared/api';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Separator } from '@/shared/ui/separator';
import { Skeleton } from '@/shared/ui/skeleton';
import { Textarea } from '@/shared/ui/textarea';

import {
  activatePromptVersion,
  activateSessionContextVersion,
  deletePromptVersion,
  getActivePrompt,
  getActiveSessionContext,
  getPromptReference,
  listPromptVersions,
  listSessionContextVersions,
  publishPrompt,
  publishSessionContext,
  savePromptDraft,
  saveSessionContextDraft,
  type PromptReference,
  type PromptReferenceStackItem,
  type PromptVersion,
} from '../../../services/agentHub';
import {
  MarkdownBlockquote,
  MarkdownEm,
  MarkdownH1,
  MarkdownH2,
  MarkdownH3,
  MarkdownH4,
  MarkdownHr,
  MarkdownLi,
  MarkdownLink,
  MarkdownOl,
  MarkdownParagraph,
  MarkdownStrong,
  MarkdownUl,
  SafeImage,
} from '../../../entities/message/ui/MarkdownComponents';
import VersionHistorySidebar from './VersionHistorySidebar';
import PublishConfirmDialog from './PublishConfirmDialog';
import { useConfirmedAction } from './useConfirmedAction';
import { useSettingsAccess } from '../access';

function ReferenceCodeBlock({ label, content }: { label: string; content: string }) {
  return (
    <div
      className="max-h-[520px] overflow-y-auto rounded-lg border border-border bg-muted p-3 font-mono text-sm whitespace-pre-wrap break-words text-foreground"
      role="region"
      aria-label={label}
    >
      {content}
    </div>
  );
}

function StackLayerDetail({ item }: { item: PromptReferenceStackItem }) {
  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold">{item.title}</h3>
        <Badge variant="outline">{item.authority}</Badge>
        <Badge variant="secondary">{item.render_location}</Badge>
        {item.editable && <Badge>Owner editable</Badge>}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>{item.scope}</span>
        <span>{item.source}</span>
        {item.version ? <span>Version {item.version}</span> : null}
        {item.overridable_by ? <span>Overridden by {item.overridable_by}</span> : null}
      </div>
      <ReferenceCodeBlock label={`${item.title} prompt text`} content={item.content} />
    </div>
  );
}

function PromptStackSkeleton() {
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(180px,260px)_minmax(0,1fr)]">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-9" />
        <Skeleton className="h-9" />
        <Skeleton className="h-9" />
      </div>
      <Skeleton className="h-[420px]" />
    </div>
  );
}

function buildFallbackStack(reference: PromptReference | null): PromptReferenceStackItem[] {
  if (!reference) return [];
  if (reference.prompt_stack?.length) return reference.prompt_stack;
  return [
    {
      key: 'platform_contract',
      title: 'Platform Contract',
      scope: 'platform_contract',
      authority: 'platform',
      source: 'runtime prompt file',
      render_location: 'session_memory',
      editable: false,
      content: reference.platform_contract.content,
      sha256: reference.platform_contract.sha256,
      version: reference.platform_contract.version,
    },
    {
      key: 'session_context',
      title: 'Session Context',
      scope: 'session_context',
      authority: 'runtime',
      source: 'request',
      render_location: 'session_memory',
      editable: false,
      content: reference.runtime_context.rendered,
      sha256: '',
    },
  ];
}

const previewPaneClassName =
  'h-100 min-h-30 box-border overflow-y-auto rounded-lg border border-border bg-muted px-3.5 py-2.5';

const previewMarkdownComponents = {
  a: MarkdownLink,
  img: SafeImage,
  blockquote: MarkdownBlockquote,
  h1: MarkdownH1,
  h2: MarkdownH2,
  h3: MarkdownH3,
  h4: MarkdownH4,
  p: MarkdownParagraph,
  ul: MarkdownUl,
  ol: MarkdownOl,
  li: MarkdownLi,
  hr: MarkdownHr,
  strong: MarkdownStrong,
  em: MarkdownEm,
};

type PromptEditorView = 'edit' | 'stack';

export default function AgentPromptEditor() {
  const { agentId = '' } = useParams<{ agentId: string }>();
  const isArgusSystemPrompt = agentId === 'argus';

  const { canAccessAgent } = useSettingsAccess();
  const canViewPromptReference = agentId ? canAccessAgent(agentId) : false;
  const [promptReference, setPromptReference] = useState<PromptReference | null>(null);
  const [loadingPromptReference, setLoadingPromptReference] = useState(false);
  const [promptReferenceError, setPromptReferenceError] = useState<string | null>(null);
  const [selectedPromptLayerKey, setSelectedPromptLayerKey] = useState('final_render_preview');
  const [activePromptView, setActivePromptView] = useState<PromptEditorView>(
    isArgusSystemPrompt ? 'stack' : 'edit',
  );

  useEffect(() => {
    if (isArgusSystemPrompt) {
      setActivePromptView('stack');
    }
  }, [isArgusSystemPrompt]);

  const loadPromptReference = useCallback(async () => {
    if (!agentId || !canViewPromptReference) {
      setPromptReference(null);
      setPromptReferenceError(null);
      return;
    }
    setLoadingPromptReference(true);
    setPromptReference(null);
    setPromptReferenceError(null);
    try {
      setPromptReference(await getPromptReference(agentId));
    } catch (err) {
      setPromptReference(null);
      setPromptReferenceError(getUserErrorMessage(err));
    } finally {
      setLoadingPromptReference(false);
    }
  }, [agentId, canViewPromptReference]);

  useEffect(() => { void loadPromptReference(); }, [loadPromptReference]);

  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [sessionContextVersions, setSessionContextVersions] = useState<PromptVersion[]>([]);
  const [activeVersionNumber, setActiveVersionNumber] = useState<number>(0);
  const [activeSessionContextVersionNumber, setActiveSessionContextVersionNumber] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingSessionContextDraft, setSavingSessionContextDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [content, setContent] = useState('');
  const [sessionContextContent, setSessionContextContent] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [sessionContextChangeSummary, setSessionContextChangeSummary] = useState('');
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [selectedSessionContextVersionId, setSelectedSessionContextVersionId] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [activatingSessionContextId, setActivatingSessionContextId] = useState<string | null>(null);

  // Edit / Preview toggle
  const [previewMode, setPreviewMode] = useState(false);
  const [sessionContextPreviewMode, setSessionContextPreviewMode] = useState(false);

  const promptStackLayers = buildFallbackStack(promptReference);
  const selectedPromptLayer =
    promptStackLayers.find((layer) => layer.key === selectedPromptLayerKey) ??
    promptStackLayers.find((layer) => layer.key === 'final_render_preview') ??
    promptStackLayers[0] ??
    null;
  const selectedLayerShowsRuntimeValues = Boolean(
    selectedPromptLayer &&
    ['session_context', 'data_context', 'artifact_context', 'runtime_directives'].includes(selectedPromptLayer.scope),
  );

  useEffect(() => {
    if (!promptStackLayers.length) return;
    if (promptStackLayers.some((layer) => layer.key === selectedPromptLayerKey)) return;
    const fallbackLayer = promptStackLayers.find((layer) => layer.key === 'final_render_preview') ?? promptStackLayers[0];
    setSelectedPromptLayerKey(fallbackLayer.key);
  }, [promptStackLayers, selectedPromptLayerKey]);

  const loadData = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const [active, versionList] = await Promise.all([
        getActivePrompt(agentId),
        listPromptVersions(agentId),
      ]);
      const [activeSessionContext, sessionContextVersionList] = await Promise.all([
        getActiveSessionContext(agentId),
        listSessionContextVersions(agentId),
      ]);
      setVersions(versionList);
      setSessionContextVersions(sessionContextVersionList);
      if (active) {
        setActiveVersionNumber(active.version_number ?? 0);
        setSelectedVersionId(active.id);
        setContent(active.content);
      } else {
        setActiveVersionNumber(0);
      }
      if (activeSessionContext) {
        setActiveSessionContextVersionNumber(activeSessionContext.version_number ?? 0);
        setSelectedSessionContextVersionId(activeSessionContext.id);
        setSessionContextContent(activeSessionContext.content);
      } else {
        setActiveSessionContextVersionNumber(0);
        setSelectedSessionContextVersionId(null);
        setSessionContextContent('');
      }
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveDraft = async () => {
    if (!agentId || !content.trim()) return;
    setSavingDraft(true);
    setError(null);
    try {
      await savePromptDraft(agentId, content, changeSummary || undefined);
      setChangeSummary('');
      toast.success('Prompt draft saved — test it in Experiment tab');
      await loadData();
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSaveSessionContextDraft = async () => {
    if (!agentId) return;
    setSavingSessionContextDraft(true);
    setError(null);
    try {
      await saveSessionContextDraft(agentId, sessionContextContent, sessionContextChangeSummary || undefined);
      setSessionContextChangeSummary('');
      toast.success('Session context draft saved');
      await loadData();
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setSavingSessionContextDraft(false);
    }
  };

  const publish = useConfirmedAction(
    async () => {
      await publishPrompt(agentId, content, changeSummary || undefined);
      setChangeSummary('');
      toast.success('Prompt published');
      await loadData();
    },
    (err) => setError(getUserErrorMessage(err)),
  );

  const publishSession = useConfirmedAction(
    async () => {
      await publishSessionContext(agentId, sessionContextContent, sessionContextChangeSummary || undefined);
      setSessionContextChangeSummary('');
      toast.success('Session context published');
      await loadData();
      await loadPromptReference();
    },
    (err) => setError(getUserErrorMessage(err)),
  );

  const deleteTargetId = useRef<string | null>(null);

  const deleteVersion = useConfirmedAction(
    async () => {
      if (!deleteTargetId.current) return;
      await deletePromptVersion(agentId, deleteTargetId.current);
      toast.success('Draft deleted');
      await loadData();
    },
    (err) => setError(getUserErrorMessage(err)),
  );

  const handleDeleteRequest = (versionId: string) => {
    deleteTargetId.current = versionId;
    deleteVersion.trigger();
  };

  const handleVersionSelect = (versionId: string) => {
    setSelectedVersionId(versionId);
    const version = versions.find((v) => v.id === versionId);
    if (version) setContent(version.content);
  };

  const handleSessionContextVersionSelect = (versionId: string) => {
    setSelectedSessionContextVersionId(versionId);
    const version = sessionContextVersions.find((v) => v.id === versionId);
    if (version) setSessionContextContent(version.content);
  };

  const handleActivate = async (versionId: string) => {
    if (!agentId) return;
    setActivatingId(versionId);
    setError(null);
    try {
      await activatePromptVersion(agentId, versionId);
      toast.success('Version activated');
      await loadData();
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setActivatingId(null);
    }
  };

  const handleActivateSessionContext = async (versionId: string) => {
    if (!agentId) return;
    setActivatingSessionContextId(versionId);
    setError(null);
    try {
      await activateSessionContextVersion(agentId, versionId);
      toast.success('Session context version activated');
      await loadData();
      await loadPromptReference();
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setActivatingSessionContextId(null);
    }
  };

  const playbookContent = (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="p-3 text-sm text-destructive">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm">Loading...</p>
      ) : (
        <div className="flex items-start gap-4">
          <div className="flex min-w-0 grow flex-col gap-3">
            {previewMode ? (
              <div className={previewPaneClassName} aria-label="Owner Playbook preview (Markdown rendered)">
                <div className="text-sm text-foreground">
                  <Markdown remarkPlugins={[remarkGfm]} components={previewMarkdownComponents}>
                    {content}
                  </Markdown>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <Label htmlFor="agent-prompt-editor-content">Owner Playbook (Markdown)</Label>
                <p className="text-xs text-muted-foreground">
                  Behavior, routing, tone, domain rules, and response style.
                </p>
                <Textarea
                  id="agent-prompt-editor-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="field-sizing-fixed h-[400px] min-h-30 resize-y overflow-y-auto font-mono text-sm leading-normal break-words whitespace-pre-wrap"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {content.split('\n').length} lines · {content.length} chars
              </p>
              <div className="flex gap-2">
                <Button
                  variant={previewMode ? 'secondary' : 'default'}
                  size="sm"
                  onClick={() => setPreviewMode(false)}
                  aria-pressed={!previewMode}
                >
                  Edit
                </Button>
                <Button
                  variant={previewMode ? 'default' : 'secondary'}
                  size="sm"
                  onClick={() => setPreviewMode(true)}
                  aria-pressed={previewMode}
                >
                  Preview
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="agent-prompt-editor-change-summary">Change Summary (optional)</Label>
              <Input
                id="agent-prompt-editor-change-summary"
                value={changeSummary}
                onChange={(e) => setChangeSummary(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={handleSaveDraft}
                disabled={savingDraft || !content.trim() || publish.pending}
              >
                {savingDraft ? 'Saving…' : 'Save Draft'}
              </Button>
              <Button
                onClick={publish.trigger}
                disabled={publish.pending || !content.trim() || savingDraft}
              >
                {publish.pending ? 'Publishing…' : 'Publish'}
              </Button>
            </div>
          </div>

          <VersionHistorySidebar
            versions={versions}
            selectedId={selectedVersionId}
            onSelect={handleVersionSelect}
            onActivate={handleActivate}
            activating={activatingId !== null}
            onDelete={handleDeleteRequest}
            deleting={deleteVersion.pending}
          />
        </div>
      )}
    </div>
  );

  const sessionContextPanel = (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="p-3 text-sm text-destructive">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm">Loading...</p>
      ) : (
        <div className="flex items-start gap-4">
          <div className="flex min-w-0 grow flex-col gap-3">
            {sessionContextPreviewMode ? (
              <div className={previewPaneClassName} aria-label="Owner Session Context preview (Markdown rendered)">
                <div className="text-sm text-foreground">
                  <Markdown remarkPlugins={[remarkGfm]} components={previewMarkdownComponents}>
                    {sessionContextContent}
                  </Markdown>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <Label htmlFor="agent-session-context-editor-content">Owner Session Context (Markdown)</Label>
                <p className="text-xs text-muted-foreground">
                  Stable CLAUDE.md starting context. Not for generated paths, uploads, or one-off request facts.
                </p>
                <Textarea
                  id="agent-session-context-editor-content"
                  value={sessionContextContent}
                  onChange={(e) => setSessionContextContent(e.target.value)}
                  className="field-sizing-fixed h-[400px] min-h-30 resize-y overflow-y-auto font-mono text-sm leading-normal break-words whitespace-pre-wrap"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {sessionContextContent.split('\n').length} lines · {sessionContextContent.length} chars
              </p>
              <div className="flex gap-2">
                <Button
                  variant={sessionContextPreviewMode ? 'secondary' : 'default'}
                  size="sm"
                  onClick={() => setSessionContextPreviewMode(false)}
                  aria-pressed={!sessionContextPreviewMode}
                >
                  Edit
                </Button>
                <Button
                  variant={sessionContextPreviewMode ? 'default' : 'secondary'}
                  size="sm"
                  onClick={() => setSessionContextPreviewMode(true)}
                  aria-pressed={sessionContextPreviewMode}
                >
                  Preview
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="agent-session-context-change-summary">Change Summary (optional)</Label>
              <Input
                id="agent-session-context-change-summary"
                value={sessionContextChangeSummary}
                onChange={(e) => setSessionContextChangeSummary(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={handleSaveSessionContextDraft}
                disabled={savingSessionContextDraft || publishSession.pending}
              >
                {savingSessionContextDraft ? 'Saving…' : 'Save Draft'}
              </Button>
              <Button
                onClick={publishSession.trigger}
                disabled={publishSession.pending || savingSessionContextDraft}
              >
                {publishSession.pending ? 'Publishing…' : 'Publish'}
              </Button>
            </div>
          </div>

          <VersionHistorySidebar
            versions={sessionContextVersions}
            selectedId={selectedSessionContextVersionId}
            onSelect={handleSessionContextVersionSelect}
            onActivate={handleActivateSessionContext}
            activating={activatingSessionContextId !== null}
          />
        </div>
      )}
    </div>
  );

  const promptLayerGuide = (
    <div className="grid gap-3 md:grid-cols-3">
      <div className="rounded-md border border-border p-3">
        <h3 className="text-sm font-semibold">Owner Playbook</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Edit behavior, identity, routing, domain heuristics, and response style here.
        </p>
      </div>
      <div className="rounded-md border border-border p-3">
        <h3 className="text-sm font-semibold">Agent Definition</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Change model, tools, MCPs, data resources, catalog text, and access-facing metadata in Config.
        </p>
      </div>
      <div className="rounded-md border border-border p-3">
        <h3 className="text-sm font-semibold">Session Context</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Use CLAUDE.md/session memory for stable starting context; turn context stays request-specific.
        </p>
      </div>
    </div>
  );

  const editContent = (
    <div className="flex flex-col gap-6">
      {promptLayerGuide}
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3" aria-labelledby="owner-playbook-editor-heading">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="owner-playbook-editor-heading" className="text-lg font-semibold">Owner Playbook</h2>
            <Badge variant="outline">
              {activeVersionNumber > 0 ? `Published v${activeVersionNumber}` : 'Manifest source'}
            </Badge>
          </div>
          {playbookContent}
        </section>
        <section className="flex flex-col gap-3" aria-labelledby="owner-session-context-editor-heading">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="owner-session-context-editor-heading" className="text-lg font-semibold">Session Context</h2>
            <Badge variant="outline">
              {activeSessionContextVersionNumber > 0
                ? `Published v${activeSessionContextVersionNumber}`
                : 'No published owner context'}
            </Badge>
          </div>
          {sessionContextPanel}
        </section>
      </div>
    </div>
  );

  const promptStackContent = (
    <div className="flex flex-col gap-4">
      {loadingPromptReference ? (
        <PromptStackSkeleton />
      ) : promptReferenceError ? (
        <p role="alert" className="rounded-md border border-border p-3 text-sm text-destructive">
          {promptReferenceError}
        </p>
      ) : promptStackLayers.length > 0 && selectedPromptLayer ? (
        <div className="flex flex-col gap-4">
          {promptLayerGuide}
          <div className="grid gap-4 lg:grid-cols-[minmax(180px,260px)_minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{promptStackLayers.length} layers</Badge>
                <Badge variant="secondary">
                  {promptReference?.rendered_prompt?.system_prompt_mode ?? 'prompt run spec'}
                </Badge>
              </div>
              <Separator />
              <ul className="flex flex-col gap-1" aria-label="Prompt stack layers">
                {promptStackLayers.map((layer) => (
                  <li key={layer.key}>
                    <Button
                      type="button"
                      variant={layer.key === selectedPromptLayer.key ? 'secondary' : 'ghost'}
                      className="h-auto w-full justify-start px-2 py-2 text-left"
                      aria-pressed={layer.key === selectedPromptLayer.key}
                      onClick={() => setSelectedPromptLayerKey(layer.key)}
                    >
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="truncate text-sm font-medium">{layer.title}</span>
                        <span className="truncate text-xs text-muted-foreground">{layer.render_location}</span>
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex min-w-0 flex-col gap-4">
              <StackLayerDetail item={selectedPromptLayer} />
              {selectedLayerShowsRuntimeValues && (promptReference?.runtime_context.values.length ?? 0) > 0 && (
                <div className="flex flex-col gap-3">
                  <h3 className="text-base font-semibold">Context Values</h3>
                  <ul aria-label="Runtime context preview values" className="flex flex-col gap-2">
                    {promptReference?.runtime_context.values.map((item) => (
                      <li key={item.key} className="flex flex-col gap-0.5 rounded-md px-2 py-1.5">
                        <span className="text-sm font-medium">{item.key}</span>
                        <span className="text-xs text-muted-foreground">
                          {item.value ?? '(empty)'} - {item.description}
                          {item.value_source ? ` Source: ${item.value_source}.` : ''}
                          {item.sensitivity ? ` Sensitivity: ${item.sensitivity}.` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <p className="rounded-md border border-border p-3 text-sm text-muted-foreground">
          No prompt stack layers were returned.
        </p>
      )}
    </div>
  );

  const promptViewHeader = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{activeVersionNumber > 0 ? `Source: Published v${activeVersionNumber}` : 'Source: Manifest'}</Badge>
        {isArgusSystemPrompt && <Badge variant="outline">Read-only</Badge>}
        {canViewPromptReference && (
          promptReferenceError ? (
            <Badge variant="destructive">Reference unavailable</Badge>
          ) : null
        )}
      </div>
      {canViewPromptReference && (
        <div className="flex rounded-lg bg-muted p-[3px]" aria-label="Prompt view selector">
          {!isArgusSystemPrompt && (
            <Button
              type="button"
              size="sm"
              variant={activePromptView === 'edit' ? 'default' : 'ghost'}
              aria-pressed={activePromptView === 'edit'}
              onClick={() => setActivePromptView('edit')}
            >
              Edit
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant={activePromptView === 'stack' ? 'default' : 'ghost'}
            aria-pressed={activePromptView === 'stack'}
            onClick={() => setActivePromptView('stack')}
          >
            Stack
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-4 pt-4">
      {promptViewHeader}
      {canViewPromptReference && (isArgusSystemPrompt || activePromptView === 'stack')
        ? promptStackContent
        : editContent}
      <PublishConfirmDialog
        isOpen={publish.showConfirm}
        onConfirm={publish.confirm}
        onCancel={publish.cancel}
        title="Publish prompt?"
      />
      <PublishConfirmDialog
        isOpen={publishSession.showConfirm}
        onConfirm={publishSession.confirm}
        onCancel={publishSession.cancel}
        title="Publish session context?"
      />
      <PublishConfirmDialog
        isOpen={deleteVersion.showConfirm}
        onConfirm={deleteVersion.confirm}
        onCancel={deleteVersion.cancel}
        title="Delete this draft?"
        description="This permanently removes the draft prompt version. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
      />
    </div>
  );
}
